export type Station = 'caja' | 'cocina' | 'pizzeria' | 'bebidas'

export interface StationDef {
  id: Station
  label: string
  icon: string
  color: string
  bg: string
  border: string
  description: string
}

export const STATIONS: StationDef[] = [
  {
    id: 'caja',
    label: 'Caja',
    icon: '💰',
    color: 'text-[#F2C94C]',
    bg: 'bg-[#F2C94C]/10',
    border: 'border-[#F2C94C]/20',
    description: 'Vista completa — pedidos, pagos, reportes',
  },
  {
    id: 'cocina',
    label: 'Cocina',
    icon: '🔥',
    color: 'text-[#00F5C9]',
    bg: 'bg-[#00F5C9]/10',
    border: 'border-[#00F5C9]/20',
    description: 'Sándwiches, wraps, cremas, bowls, niños',
  },
  {
    id: 'pizzeria',
    label: 'Pizzería',
    icon: '🍕',
    color: 'text-[#7A5CFF]',
    bg: 'bg-[#7A5CFF]/10',
    border: 'border-[#7A5CFF]/20',
    description: 'Pizzas y productos de pizzería',
  },
  {
    id: 'bebidas',
    label: 'Bebidas',
    icon: '🥤',
    color: 'text-[#00F5C9]',
    bg: 'bg-[#00F5C9]/10',
    border: 'border-[#00F5C9]/20',
    description: 'Bebidas, smoothies, batidos',
  },
]

// Station detection by product station field (from Supabase products table)
// or fallback heuristics on order_text lines

const COCINA_KEYWORDS = [
  'sándwich',
  'sandwich',
  'wrap',
  'crema',
  'bowl',
  'pollo',
  'niño',
  'dedos',
  'snack',
]
const BEBIDAS_KEYWORDS = [
  'natural',
  'smoothie',
  'coca',
  'fanta',
  'sprite',
  'agua',
  'chocolate',
  'aguadulce',
  'tropical',
  'leche',
  'batido',
]
const PIZZERIA_KEYWORDS = ['pizza', 'pizz']

/**
 * Determine if an item line from order_text belongs to a station.
 * Uses the station field if available in the line, otherwise falls back to keyword matching.
 */
export function itemBelongsToStation(
  itemLine: string,
  station: Station,
): boolean {
  if (station === 'caja') return true

  const normalizedLine = itemLine.toLowerCase().trim()

  // Check for explicit station tag in the line (e.g. "[cocina]")
  const stationTagMatch = normalizedLine.match(/\[(\w+)\]/)
  if (stationTagMatch) {
    return stationTagMatch[1] === station
  }

  // Fallback: keyword heuristics
  if (station === 'cocina') {
    return COCINA_KEYWORDS.some((kw) => normalizedLine.includes(kw))
  }
  if (station === 'bebidas') {
    return BEBIDAS_KEYWORDS.some((kw) => normalizedLine.includes(kw))
  }
  if (station === 'pizzeria') {
    return PIZZERIA_KEYWORDS.some((kw) => normalizedLine.includes(kw))
  }

  return false
}

/** Parse order_text to extract item lines */
export function parseOrderItems(orderText: string): string[] {
  if (!orderText) return []
  const lines = orderText.split('\n')
  return lines.filter(
    (line) => /x\d+\s*—\s*[₡$¢]/.test(line) || /x\d+\s*—\s*\$/.test(line),
  )
}

/** Check if an order has items for a specific station */
export function orderHasItemsForStation(
  orderText: string,
  station: Station,
): boolean {
  if (station === 'caja') return true
  const items = parseOrderItems(orderText)
  return items.some((item) => itemBelongsToStation(item, station))
}

/** Get only the items relevant to a station from an order */
export function getStationItems(orderText: string, station: Station): string[] {
  if (station === 'caja') return parseOrderItems(orderText)
  const items = parseOrderItems(orderText)
  return items.filter((item) => itemBelongsToStation(item, station))
}

/** Count items for a station across all orders */
export function countStationItems(
  orders: { order_text: string; status: string }[],
  station: Station,
): number {
  const activeStatuses = new Set(['new', 'accepted', 'preparing', 'ready'])
  return orders.filter(
    (o) =>
      activeStatuses.has(o.status) &&
      orderHasItemsForStation(o.order_text, station),
  ).length
}
